import { API_ORIGIN, authorizedFetch, FIT_POLL_MS } from '@yeshuman/sdk'
import { useQuery } from '@tanstack/react-query'


export function jobTalentSummaryQueryKey(opportunityId: string) {
  return ['job-talent-summary', opportunityId] as const
}

export type JobTalentRecommendation = {
  profile_id: string
  application_id: string
  candidate_name: string
  status: string
  final_score: number | null
  rank: number | null
}

export type JobTalentRankedPerson = {
  application_id: string
  profile_id: string
  candidate_name: string
  status: string
  final_score: number | null
  rank: number | null
}

export type JobTalentSummary = {
  opportunity_id: string
  cohort_summary: string
  cohort_summary_at: string | null
  cohort_membership_fingerprint: string
  top_recommendations: JobTalentRecommendation[]
  ranked: JobTalentRankedPerson[]
  total: number
}

async function fetchJobTalentSummary(opportunityId: string): Promise<JobTalentSummary> {
  const response = await authorizedFetch(
    `${API_ORIGIN}/api/opportunities/managed/${encodeURIComponent(opportunityId)}/talent-summary/`,
  )
  if (!response.ok) {
    throw new Error(`Failed to load talent summary (${response.status})`)
  }
  return response.json() as Promise<JobTalentSummary>
}

export function useJobTalentSummary(
  opportunityId: string | undefined,
  enabled = true,
  options: { refetchWhileUnscored?: boolean } = {},
) {
  const { refetchWhileUnscored = false } = options
  return useQuery({
    queryKey: jobTalentSummaryQueryKey(opportunityId ?? ''),
    queryFn: () => fetchJobTalentSummary(opportunityId!),
    enabled: !!opportunityId && enabled,
    staleTime: 30 * 1000,
    refetchInterval: refetchWhileUnscored ? FIT_POLL_MS : false,
  })
}
